import { date, ShapeName } from '@squaredup/data-streams';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { useVisualizationTheme } from 'dashboard-engine/visualisations/hooks/useVisualizationTheme';
import { useMemo } from 'react';
import { barGraphDefaultRightMargin, DataStreamBarChartConfig } from '../Config';
import { ResolvedGroupedDataObject } from '../dataUtils';
import { generateGroupedBars, generateStackedBars } from './bars';
import {
    calculateBottomMargin,
    calculateHorizontalTicks,
    calculateLeftMargin,
    calculateRightMargin,
    calculateVerticalTicks,
    shouldRotateTicks
} from './ticks';

export const useBars = ({
    config,
    series,
    dataKeys,
    isHorizontal,
    isGrouping,
    isPercentage,
    showTotals,
    showXAxisLabel,
    showYAxisLabel,
    showLabels,
    xAxisColumnShape,
    yAxisWidth,
    valueFormatter,
    xAxisTickFormatter,
    yAxisTickFormatter,
    width,
    height
}: {
    config: DataStreamBarChartConfig;
    series: ResolvedGroupedDataObject[];
    dataKeys: string[];
    isHorizontal: boolean;
    isGrouping: boolean;
    isPercentage: boolean;
    showTotals: boolean;
    showLabels: boolean;
    showXAxisLabel: boolean;
    showYAxisLabel: boolean;
    yAxisWidth: number;
    xAxisColumnShape: ShapeName;
    valueFormatter: (v: unknown) => string;
    xAxisTickFormatter: (value: unknown) => string;
    yAxisTickFormatter: (value: string | number | Date) => string;
    width: number;
    height: number;
}) => {
    const barGraphTheme = useVisualizationTheme('bar');

    let maxValue: 'auto' | number = 'auto';
    let minValue: 'auto' | number = 'auto';

    if (config.range?.type === 'custom') {
        maxValue = config.range?.maximum ?? 'auto';
        minValue = config.range?.minimum ?? 'auto';
    }

    const marginTop = showTotals && config.horizontalLayout !== 'horizontal' ? 20 : 6;

    return useMemo(() => {
        const marginBottom = 20;
        let innerHeight = height - marginTop - marginBottom;

        // Scale used for the categories (x-axis in vertical mode, y-axis in horizontal mode)
        const categoryScale = scaleBand<string>({
            domain: series.map((d) => d.__group__ ?? ''),
            range: isHorizontal ? [innerHeight, 0] : [0, width],
            padding: 0.2,
            round: true
        });

        // Calculate the total value for each group in the series, to be used for total labels and margin calculcation
        const totalValues = isGrouping
            ? series.flatMap((d) => dataKeys.map((key): number => d[key] ?? 0))
            : series.map((d) => dataKeys.map((key): number => d[key] ?? 0).reduce((a, b) => a + b, 0));

        const totalLabels = showTotals ? totalValues.map((total) => valueFormatter(total)) : [];

        const maxTotalValue = Math.max(...totalValues);
        const minValueForScale = Math.min(...totalValues, 0);

        const marginRight = isHorizontal && showTotals ? calculateRightMargin(totalLabels) : barGraphDefaultRightMargin;

        // Scale used for what we're grouping by, to be displayed either stacked or grouped bars
        const groupScale = scaleBand<string>({
            domain: dataKeys,
            range: [0, categoryScale.bandwidth()]
        });

        // Scale used for the values (y-axis in vertical mode, x-axis in horizontal mode)
        const valueScale = scaleLinear<number>({
            domain: [minValue === 'auto' ? minValueForScale : minValue, maxValue === 'auto' ? maxTotalValue : maxValue],
            range: isHorizontal ? [0, width - yAxisWidth - marginRight] : [innerHeight, marginTop],
            nice: true,
            clamp: true
        });

        const colorScale = scaleOrdinal<string, string>({
            domain: dataKeys,
            range: barGraphTheme
        });

        const marginLeft = isHorizontal
            ? yAxisWidth
            : calculateLeftMargin(valueScale, yAxisTickFormatter, showYAxisLabel);

        const innerWidth = width - marginLeft - marginRight;

        // With the new marginLeft, we need to adjust the category scale range
        if (isHorizontal) {
            valueScale.range([0, innerWidth]);
        } else {
            categoryScale.range([0, innerWidth]);
        }

        const xAxisTickPositions = isHorizontal
            ? calculateHorizontalTicks({
                  scale: valueScale,
                  graphWidth: innerWidth,
                  formatter: xAxisTickFormatter
              })
            : calculateVerticalTicks({
                  series,
                  isDateColumn: xAxisColumnShape === date.name,
                  scale: categoryScale,
                  graphWidth: innerWidth
              });

        // Determine if rotation is needed based on the actual ticks that will be displayed
        const rotateXAxisTicks = shouldRotateTicks(xAxisTickPositions, innerWidth, marginLeft);

        // Get the labels used on this axis for margin calculation
        const xAxisTicks = xAxisTickPositions.map((t) => t.tick);

        // Calculate dynamic margin based on these labels and rotation
        const newBottomMargin = calculateBottomMargin(rotateXAxisTicks, xAxisTicks, showXAxisLabel);

        innerHeight = height - marginTop - newBottomMargin;

        // Adjust relevant scale based on the recalculated innerHeight
        if (!isHorizontal) {
            valueScale.range([innerHeight, marginTop]);
        } else {
            categoryScale.range([innerHeight, 0]);
        }

        const labelGenerator = showLabels
            ? (barData: ResolvedGroupedDataObject, key: string) =>
                  isPercentage
                      ? `${valueFormatter(barData[key])} (${barData[`${key}-percentage`]})`
                      : valueFormatter(barData[key])
            : () => '';

        const bars = isGrouping
            ? generateGroupedBars({
                  series,
                  dataKeys,
                  isHorizontal,
                  labelGenerator,
                  valueScale,
                  categoryScale,
                  groupScale,
                  colorScale
              })
            : generateStackedBars({
                  series,
                  dataKeys,
                  isHorizontal,
                  labelGenerator,
                  valueScale,
                  categoryScale,
                  colorScale
              });

        const totals = series.map((seriesItem, i) => ({
            id: seriesItem.__group__ ?? '',
            label: totalLabels[i],
            width: categoryScale.bandwidth(),
            x: isHorizontal ? valueScale(totalValues[i]) : categoryScale(seriesItem.__group__ ?? '') || 0,
            y: isHorizontal ? categoryScale(seriesItem.__group__ ?? '') || 0 : valueScale(totalValues[i])
        }));

        return {
            bars,
            totals,
            categoryScale,
            groupScale,
            valueScale,
            colorScale,
            xAxisTickPositions,
            rotateXAxisTicks,
            margin: {
                top: marginTop,
                right: marginRight,
                bottom: newBottomMargin,
                left: marginLeft
            }
        };
    }, [
        height,
        marginTop,
        series,
        isHorizontal,
        width,
        isGrouping,
        showTotals,
        showLabels,
        dataKeys,
        minValue,
        maxValue,
        yAxisWidth,
        barGraphTheme,
        xAxisColumnShape,
        showXAxisLabel,
        showYAxisLabel,
        isPercentage,
        yAxisTickFormatter,
        xAxisTickFormatter,
        valueFormatter
    ]);
};
